import { ArrowRight01Icon, SquareLock02Icon } from '@hugeicons/react'
import clsx from 'clsx'
import { DateTime } from 'luxon'

import type { AllowedSidebarNavItem } from '@/shared/hooks/useNavigation'
import { Link } from '@/shared/ui/Link'

import { getAppMenuDisplayName, groupAppMenuNavItems } from './appMenuNav'
import { useAppMenu } from './AppMenuContext'

type StartAtValue =
  | string
  | { toLocaleString: (format: Intl.DateTimeFormatOptions) => string }
  | null

type AppMenuNavSectionsProps = {
  items: AllowedSidebarNavItem[]
  basePathWithId: string
  startAt: StartAtValue
}

const navLinkClass =
  'flex w-full cursor-pointer items-center gap-2 rounded-lg border-0 bg-transparent px-2 py-1.5 text-[13px] font-semibold leading-tight text-gray-700 no-underline hover:bg-[#f8f9fa]'

function formatLockStartAt(startAt: StartAtValue) {
  if (!startAt) return ''

  if (typeof startAt === 'string') {
    return DateTime.fromISO(startAt).toLocaleString(DateTime.DATETIME_MED)
  }

  return startAt.toLocaleString(DateTime.DATETIME_MED)
}

function NavRow({
  item,
  basePathWithId,
  startAt
}: {
  item: AllowedSidebarNavItem
  basePathWithId: string
  startAt: StartAtValue
}) {
  const { close } = useAppMenu()
  const Icon = item.icon
  const label = getAppMenuDisplayName(item)

  if (!item.allowed) {
    return (
      <li className="m-0">
        <span
          title={`Bloqueado até o início do evento em ${formatLockStartAt(startAt)}`}
          className={clsx(
            navLinkClass,
            'cursor-not-allowed text-[#b5b5c3] hover:bg-transparent'
          )}
        >
          <SquareLock02Icon size={18} variant="duotone" />
          {label}
        </span>
      </li>
    )
  }

  return (
    <li className="m-0">
      <Link
        href={`${basePathWithId}${item.href}`}
        className={clsx(
          navLinkClass,
          item.active &&
            'bg-[color-mix(in_srgb,var(--color-brand)_10%,#fff)] text-[var(--color-brand)]'
        )}
        onClick={close}
      >
        <Icon size={18} variant={item.active ? 'solid' : 'duotone'} />
        {label}
        <ArrowRight01Icon
          size={14}
          className={clsx(
            'ml-auto',
            item.active ? 'text-[var(--color-brand)]' : 'text-[#d1d3e0]'
          )}
        />
      </Link>
    </li>
  )
}

export function AppMenuNavSections({
  items,
  basePathWithId,
  startAt
}: AppMenuNavSectionsProps) {
  const groups = groupAppMenuNavItems(items)

  const sectionTitleClass =
    'm-0 mb-1.5 px-2 text-2xs font-extrabold tracking-wider text-[#a1a5b7]'

  return (
    <div className="px-3.5 pt-3 pb-1">
      {groups.principal.length > 0 && (
        <>
          <p className={sectionTitleClass}>PRINCIPAL</p>
          <ul className="m-0 mb-3.5 list-none p-0">
            {groups.principal.map(item => (
              <NavRow
                key={item.slug}
                item={item}
                basePathWithId={basePathWithId}
                startAt={startAt}
              />
            ))}
          </ul>
        </>
      )}

      {groups.suporte.length > 0 && (
        <>
          <p className={sectionTitleClass}>SUPORTE</p>
          <ul className="m-0 mb-3.5 list-none p-0">
            {groups.suporte.map(item => (
              <NavRow
                key={item.slug}
                item={item}
                basePathWithId={basePathWithId}
                startAt={startAt}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  )
}
